import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password) => api.post('/auth/login', { username, password });
export const register = (username, password) => api.post('/auth/register', { username, password });
export const getMe = () => api.get('/auth/me');

// Teams
export const getTeams = () => api.get('/teams');
export const createTeam = (name) => api.post('/teams', { name });
export const deleteTeam = (id) => api.delete(`/teams/${id}`);

// Draw
export const performDraw = () => api.post('/draw');
export const resetDraw = () => api.post('/draw/reset');
export const getDrawSequence = () => api.get('/draw/sequence');

// Groups
export const getGroups = () => api.get('/groups');

// Players
export const getPlayers = (teamId) => api.get('/players', { params: { team_id: teamId } });
export const checkHomonymy = (firstName, lastName) => api.get('/players/check-homonymy', { params: { first_name: firstName, last_name: lastName } });
export const createPlayer = (data) => api.post('/players', data);
export const updatePlayer = (id, data) => api.put(`/players/${id}`, data);
export const deletePlayer = (id) => api.delete(`/players/${id}`);

// Sanctions
export const getSanctions = (teamId, matchId) => api.get('/sanctions', { params: { team_id: teamId, match_id: matchId } });
export const createSanction = (data) => api.post('/sanctions', data);
export const updateSanction = (id, data) => api.put(`/sanctions/${id}`, data);
export const deleteSanction = (id) => api.delete(`/sanctions/${id}`);

// Matches
export const getMatches = (phase, groupId) => api.get('/matches', { params: { phase, group_id: groupId } });
export const createMatch = (data) => api.post('/matches', data);
export const updateMatch = (id, data) => api.put(`/matches/${id}`, data);
export const swapMatchSlots = (matchId, otherId) => api.post(`/matches/${matchId}/swap-slot/${otherId}`);
export const resetMatchResult = (id) => api.post(`/matches/${id}/reset-result`);
export const recalculateTeamStats = () => api.post('/teams/recalculate-stats');
export const realignKnockoutDates = () => api.post('/knockout/realign-dates');

// Goals
export const getMatchGoals = (matchId) => api.get(`/goals/match/${matchId}`);
export const getGoalsLeaderboard = () => api.get('/goals/leaderboard');
export const deleteMatch = (id) => api.delete(`/matches/${id}`);

// Calendar
export const generateCalendar = () => api.post('/calendar/generate');
export const reassignCalendarDates = () => api.post('/calendar/reassign-dates');

// Knockout Stage
export const getKnockout = () => api.get('/knockout');
export const generateKnockout = (picked_first_group, picked_second_group) =>
  api.post('/knockout/generate', null, { params: { picked_first_group, picked_second_group } });
export const advanceKnockout = () => api.post('/knockout/advance');
export const getThirdsStandings = () => api.get('/standings/thirds');

// Menu
export const getMenu = () => api.get('/menu');
export const uploadMenuImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/menu/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteMenuImage = (id) => api.delete(`/menu/${id}`);

// Logo
export const getLogo = () => api.get('/logo');
export const uploadLogo = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/logo/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteLogo = () => api.delete('/logo');

// Sponsors
export const getSponsors = () => api.get('/sponsors');
export const getAllSponsors = () => api.get('/sponsors/all');
export const createSponsor = (data) => api.post('/sponsors', data);
export const updateSponsor = (id, data) => api.put(`/sponsors/${id}`, data);
export const deleteSponsor = (id) => api.delete(`/sponsors/${id}`);
export const uploadSponsorLogo = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/sponsors/${id}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);

// Champions (Albo d'oro)
export const getChampions = () => api.get('/champions');
export const createChampion = (data) => api.post('/champions', data);
export const updateChampion = (id, data) => api.put(`/champions/${id}`, data);
export const deleteChampion = (id) => api.delete(`/champions/${id}`);
export const uploadChampionPhoto = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/champions/${id}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Match photos (Galleria partite)
export const getMatchPhotos = (limit = 200) => api.get(`/match-photos?limit=${limit}`);
export const uploadMatchPhoto = (file, meta = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (meta.title) formData.append('title', meta.title);
  if (meta.description) formData.append('description', meta.description);
  if (meta.match_id) formData.append('match_id', meta.match_id);
  if (meta.team_ids && meta.team_ids.length) {
    formData.append('team_ids', JSON.stringify(meta.team_ids));
  }
  if (meta.taken_at) formData.append('taken_at', meta.taken_at);
  return api.post('/match-photos', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const updateMatchPhoto = (id, data) => api.put(`/match-photos/${id}`, data);
export const deleteMatchPhoto = (id) => api.delete(`/match-photos/${id}`);

// Stats
export const getStats = () => api.get('/stats');

export default api;
